import React from "react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { DealerPageData, ExtractSwarovskiPageData } from "@swo/storefront";
import { DealerCardDetails } from "../DealerLocatorComponent/subcomponents/SelectedDealer";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import { getNavigationLink } from "../DealerLocatorComponent/google-maps/get-navigation-link";
import { CSS_MQ } from "../../styles/breakpoints";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";

function parseTime(iso8601Time: string): string | undefined {
  const date = new Date("2020-07-28T" + iso8601Time);
  if (isNaN(date.getTime())) return;
  return ("0" + date.getUTCHours()).slice(-2) + ":" + ("0" + date.getUTCMinutes()).slice(-2);
}

function getDayLabel(dayIdx: number): string | undefined {
  return ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"][dayIdx];
}

export const DealerDetailsContact: ReactTFC<{
  dealerDetails: ExtractSwarovskiPageData<DealerPageData>["data"]["dealerDetails"];
  translations: ExtractSwarovskiPageData<DealerPageData>["data"]["translations"];
  dealerLocatorLink: string;
  navigateLabel: string;
}> = props => {
  return (
    <React.Fragment>
      <div
        css={css`
          grid-area: contact;

          ${CSS_MQ.atLeastLg} {
            padding-left: 20px;
          }
        `}
      >
        <span css={headline}>{props.translations.contactLabel.value}</span>

        <DealerCardDetails
          representativeName={props.dealerDetails.contact.representativeName}
          address={props.dealerDetails.address?.label || []}
          internationalPhoneNumber={props.dealerDetails.address?.internationalPhoneNumber}
          email={props.dealerDetails.address?.email}
          website={props.dealerDetails.contact.website}
          attachStyles={css`
            margin-top: 20px;
            margin-bottom: ${CSS_SPACER_SIZES.md};
          `}
        />

        {props.dealerDetails.address?.coordinates && (
          <span
            css={css`
              display: block;
              margin-bottom: ${CSS_SPACER_SIZES.md};
            `}
          >
            <SwarovskiButton
              variant={"green"}
              as={"a"}
              target={"_blank"}
              rel={"noopener noreferrer"}
              isInternal={false}
              link={getNavigationLink(props.dealerDetails.address.coordinates)}
            >
              {props.navigateLabel}
            </SwarovskiButton>
          </span>
        )}
      </div>

      <div
        css={css`
          grid-area: opening-hours;
          ${CSS_MQ.atLeastLg} {
            padding-left: 20px;
          }
        `}
      >
        {props.dealerDetails.openingHours.length > 0 && (
          <React.Fragment>
            <hr
              css={css`
                height: 1px;
                width: 100%;
                max-width: 500px;

                border: none;

                background-color: ${CSS_COLORS.Grey5};
                margin-left: 0;
                margin-top: 0;
                margin-bottom: ${CSS_SPACER_SIZES.md};
                display: block;
              `}
            />

            <span css={headline}>{props.translations.openingHoursLabel.value}</span>
            <ul css={listStyles}>
              {props.dealerDetails.openingHours
                .sort((a, b) => a.day - b.day)
                .map((entry, idx) => {
                  const dayLabel = getDayLabel(entry.day);
                  const openTime = parseTime(entry.openTime);
                  const closeTime = parseTime(entry.closeTime);
                  if (dayLabel && openTime && closeTime) {
                    return (
                      <li
                        css={css`
                          ${styles};

                          display: grid;
                          grid-template-columns: 100px auto;
                        `}
                        key={idx}
                      >
                        <span
                          css={css`
                            text-transform: uppercase;
                          `}
                        >
                          {dayLabel}
                        </span>
                        <span>{`${openTime} - ${closeTime}`}</span>
                      </li>
                    );
                  } else {
                    return null;
                  }
                })}
            </ul>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

const styles = css`
  ${FONTS.SwarovskiFutura300};

  font-size: 21px;
  ${fontColor.black};

  padding-bottom: 15px;

  a {
    color: ${CSS_COLORS.Black};
  }
`;

const headline = [
  FONTS.SwarovskiFutura500,
  fontColor.black,
  fontSize.x2,
  css`
    text-transform: uppercase;
    margin-bottom: 20px;
    display: block;
  `,
];
const listStyles = css`
  margin-bottom: 50px;
`;
